import { TMasterStandardIndustryClassification } from "@/api/entities/master-standard-industry-classification.entity";
import { TMasterStandardIndustryGroup } from "@/api/entities/master-standard-industry-group.entity";
import { useGetStandardIndustryClassificationList } from "@/api/services/global/master";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Switch } from "@/components/elements/Switch";
import { TopPage } from "@/components/elements/TopPage";
import { EMPTY_STR } from "@/constants/string";
import { ClassificationFormModal } from "@/features/master-data/components/StandardIndustry/ClassificationFormModal";
import { ClassificationNodeModal } from "@/features/master-data/components/StandardIndustry/ClassificationNodeModal";
import { HiddenClassificationModal } from "@/features/master-data/components/StandardIndustry/HiddenClassificationModal";
import { StandardIndustryStyledTable } from "@/features/master-data/components/StandardIndustry/standardIndustryTable.styles";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { STANDARD_INDUSTRY_LEVEL, STANDARD_INDUSTRY_LEVEL_LABEL } from "@/features/master-data/constants/standardIndustry";
import { useVisible } from "@/hooks/useVisible";
import { background, text } from "@/theme/colors";
import { toNumber } from "@/utils/number";
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";

type TStandardIndustryClassificationTableProps = {
  group: TMasterStandardIndustryGroup;
  onBack: () => void;
};

export const StandardIndustryClassificationTable: FC<TStandardIndustryClassificationTableProps> = ({ group, onBack }) => {
  const createMajorModal = useVisible();
  const [editingMajor, setEditingMajor] = useState<TMasterStandardIndustryClassification>();
  const [hiddenMajor, setHiddenMajor] = useState<TMasterStandardIndustryClassification>();

  const { data: classifications, isLoading } = useGetStandardIndustryClassificationList({
    groupId: toNumber(group.id),
    nested: true,
  });

  // Index every node (MAJOR/MINOR/DETAIL) by id from the nested tree so children can be looked up at any level.
  const nodeById = useMemo(() => {
    const map = new Map<number, TMasterStandardIndustryClassification>();
    const walk = (nodes: TMasterStandardIndustryClassification[]) => {
      nodes.forEach((node) => {
        map.set(toNumber(node.id), node);
        if (node.children?.length) {
          walk(node.children);
        }
      });
    };
    walk(classifications);
    return map;
  }, [classifications]);

  const getChildren = useCallback(
    (parentId: number) => (nodeById.get(parentId)?.children ?? []).slice().sort((a, b) => a.order - b.order),
    [nodeById],
  );

  const majorNodes = useMemo(
    () => classifications.filter((node) => node.level === STANDARD_INDUSTRY_LEVEL.MAJOR).sort((a, b) => a.order - b.order),
    [classifications],
  );

  const majorLevelLabel = STANDARD_INDUSTRY_LEVEL_LABEL[STANDARD_INDUSTRY_LEVEL.MAJOR];
  const minorLevelLabel = STANDARD_INDUSTRY_LEVEL_LABEL[STANDARD_INDUSTRY_LEVEL.MINOR];

  return (
    <>
      <TopPage title={`「${MASTER_DATA_TYPE_TITLE.STANDARD_INDUSTRY}」大分類 ${group.code} ${group.name} の編集`} enablePageBack={false} />
      <InnerBox gap={2}>
        <Stack direction="row" justifyContent="flex-end" mb={1}>
          <Button size="sm" variant="primary" onClick={() => createMajorModal.open()}>
            {`${majorLevelLabel}追加`}
          </Button>
        </Stack>

        {isLoading ? (
          <LoadingPage />
        ) : (
          <StandardIndustryStyledTable>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{`${majorLevelLabel}コード`}</TableCell>
                  <TableCell>{majorLevelLabel}</TableCell>
                  <TableCell>{minorLevelLabel}</TableCell>
                  <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "100px", bgcolor: background.white }}>
                    有効／無効
                  </TableCell>
                  <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "0", bgcolor: background.white }}>編集</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {majorNodes.map((row) => {
                  const minors = getChildren(toNumber(row.id));
                  return (
                    <TableRow key={row.id}>
                      <TableCell sx={{ minWidth: "120px", paddingLeft: "8px !important" }}>
                        <Typography component="p" color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                          {row.fullCode || EMPTY_STR.TEXT}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: "220px", paddingLeft: "8px !important" }}>
                        <Typography component="p" color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                          {row.name || EMPTY_STR.TEXT}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: "360px" }}>
                        {minors.length > 0
                          ? minors.map((minor, minorIndex) => (
                              <Box
                                key={minor.id}
                                sx={{
                                  lineHeight: "40px",
                                  height: "40px",
                                  borderBottom: minorIndex !== minors.length - 1 ? "1px solid #E6E7E8" : "none",
                                  paddingLeft: "4px",
                                }}
                              >
                                <Typography color={minor.isHidden ? text.tertiary : text.primary} variant="body14">
                                  {`${minor.fullCode}　${minor.name}` || EMPTY_STR.TEXT}
                                </Typography>
                              </Box>
                            ))
                          : EMPTY_STR.TEXT}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center !important",
                          minWidth: "120px",
                          position: "sticky",
                          right: "100px",
                          bgcolor: background.white,
                        }}
                      >
                        <Switch sx={{ marginRight: 0, marginLeft: 0 }} checked={!row.isHidden} onChange={() => setHiddenMajor(row)} color="primary" />
                      </TableCell>
                      <TableCell
                        sx={{
                          minWidth: "100px",
                          textAlign: "center !important",
                          paddingX: "4px !important",
                          position: "sticky",
                          right: "0px",
                          bgcolor: background.white,
                        }}
                      >
                        <Button size="sm" onClick={() => setEditingMajor(row)}>
                          編集
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StandardIndustryStyledTable>
        )}
      </InnerBox>

      <FooterActionBar mt={4}>
        <Stack sx={{ flexDirection: "row", justifyContent: "flex-start" }}>
          <Button variant="ghost" size="sm" onClick={onBack}>
            戻る
          </Button>
        </Stack>
      </FooterActionBar>

      {createMajorModal.visible && (
        <ClassificationFormModal
          isOpen={createMajorModal.visible}
          onClose={createMajorModal.close}
          groupId={toNumber(group.id)}
          parentId={null}
          level={STANDARD_INDUSTRY_LEVEL.MAJOR}
          parentFullCode=""
        />
      )}

      {editingMajor && (
        <ClassificationNodeModal
          isOpen={Boolean(editingMajor)}
          onClose={() => setEditingMajor(undefined)}
          node={editingMajor}
          groupId={toNumber(group.id)}
          getChildren={getChildren}
        />
      )}

      <HiddenClassificationModal hiddenClassification={hiddenMajor} setHiddenClassification={setHiddenMajor} />
    </>
  );
};
